import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { SellingMode } from "./enums/sellingMode";
import {
  getPerItemQuantityFieldLabel,
  getPricePerUnitSuffix
} from "./utils/getProductInventoryStatCardDescription";
import { Product } from "./types/products";

export interface ProductItemBatchSummaryProps {
  product: Pick<Product, "sellingMode">;
  labelCount: number;
  perItemQuantity: number;
  quantityUnitLabel: string;
  variationName?: string;
  purchasePrice?: number;
  salesPrice?: number;
  /** When true, omit price rows (clone flow shows them in the source banner). */
  hidePrices?: boolean;
}

const ProductItemBatchSummary: React.FC<ProductItemBatchSummaryProps> = ({
  product,
  labelCount,
  perItemQuantity,
  quantityUnitLabel,
  variationName,
  purchasePrice,
  salesPrice,
  hidePrices = false
}) => {
  const { t } = useKivunovaTranslation();
  const showPerItemQuantity = product.sellingMode !== SellingMode.SINGLE_ITEM;
  const totalUnits = labelCount * perItemQuantity;
  const pricePerSuffix = getPricePerUnitSuffix(
    product.sellingMode,
    quantityUnitLabel,
    t
  );

  return (
    <Paper
      variant="outlined"
      data-testid="product-item-batch-summary"
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: "grey.50"
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
        {t("product-item-batch-summary-title", "Summary")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {variationName ? (
          <Typography variant="body2">
            <strong>
              {t("product-item-batch-summary-label-name", "Item name")}:
            </strong>{" "}
            {variationName}
          </Typography>
        ) : null}
        {!hidePrices && purchasePrice != null ? (
          <Typography variant="body2">
            <strong>
              {t(
                "product-item-batch-summary-purchase-price",
                "Purchase price ({{suffix}})",
                { suffix: pricePerSuffix }
              )}
              :
            </strong>{" "}
            {purchasePrice}
          </Typography>
        ) : null}
        {!hidePrices && salesPrice != null ? (
          <Typography variant="body2">
            <strong>
              {t(
                "product-item-batch-summary-sales-price",
                "Sale price ({{suffix}})",
                { suffix: pricePerSuffix }
              )}
              :
            </strong>{" "}
            {salesPrice}
          </Typography>
        ) : null}
        {showPerItemQuantity ? (
          <Typography variant="body2">
            <strong>
              {getPerItemQuantityFieldLabel(
                product.sellingMode,
                quantityUnitLabel,
                t
              )}
              :
            </strong>{" "}
            {perItemQuantity}
            {product.sellingMode === SellingMode.MEASURED
              ? ""
              : ` ${quantityUnitLabel}`}
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          sx={{ mt: 0.5 }}
          color="primary.main"
          fontWeight={600}
        >
          {labelCount === 1
            ? t(
                "product-item-batch-summary-creating-one",
                "Creating 1 new item (each with its own QR code)"
              )
            : t(
                "product-item-batch-summary-creating-many",
                "Creating {{count}} new items (each with its own QR code)",
                { count: labelCount }
              )}
        </Typography>
        {showPerItemQuantity ? (
          <Typography variant="caption" color="text.secondary">
            {t(
              "product-item-batch-summary-total-stock",
              "Total stock added: {{labelCount}} × {{perItemQuantity}} = {{totalUnits}} {{quantityUnitLabel}}",
              {
                labelCount,
                perItemQuantity,
                totalUnits,
                quantityUnitLabel
              }
            )}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

export default ProductItemBatchSummary;
